/**
 * Pre-drawn images the renderer stamps every frame: soft glows, cube icons, emoji and
 * name tags. Each is drawn once (per colour / size) into an offscreen canvas.
 */
import { createLayer, SpriteCache } from './canvas';
import { parseHex } from './color';
import { type CubeLook, drawCubeIcon } from './icons';

const GLOW_SIZE = 64;
const glows = new SpriteCache(48);

/**
 * A soft round glow in `color` (CSS hex): bright centre, fading to transparent at the edge.
 * Stamp it with the 'lighter' blend for a neon look. Null outside a browser.
 */
export function glowSprite(color: string): HTMLCanvasElement | null {
  return glows.get(color, () => {
    const layer = createLayer(GLOW_SIZE, GLOW_SIZE);
    if (!layer) return null;
    const { ctx } = layer;
    const r = GLOW_SIZE / 2;
    const [cr, cg, cb] = parseHex(color);
    const gradient = ctx.createRadialGradient(r, r, 0, r, r, r);
    gradient.addColorStop(0, `rgba(${cr}, ${cg}, ${cb}, 0.9)`);
    gradient.addColorStop(0.35, `rgba(${cr}, ${cg}, ${cb}, 0.45)`);
    gradient.addColorStop(0.7, `rgba(${cr}, ${cg}, ${cb}, 0.12)`);
    gradient.addColorStop(1, `rgba(${cr}, ${cg}, ${cb}, 0)`);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, GLOW_SIZE, GLOW_SIZE);
    return layer.canvas;
  });
}

/** Sprite sizes are rounded up to this step (device px) so zooming does not redraw them. */
const SIZE_STEP = 8;

export function spriteSize(devicePx: number): number {
  return Math.max(SIZE_STEP, Math.ceil(devicePx / SIZE_STEP) * SIZE_STEP);
}

/**
 * One sprite at the few sizes in use (views of different zoom draw the same racer at
 * different sizes every frame): drawn once per size step, the oldest size forgotten first.
 */
export class SizedSprite {
  private readonly bySize = new Map<number, HTMLCanvasElement | null>();

  constructor(
    private readonly draw: (size: number) => HTMLCanvasElement | null,
    private readonly limit = 6,
  ) {}

  get(devicePx: number): HTMLCanvasElement | null {
    const size = spriteSize(devicePx);
    let sprite = this.bySize.get(size);
    if (sprite === undefined) {
      sprite = this.draw(size);
      if (this.bySize.size >= this.limit) {
        const oldest = this.bySize.keys().next().value;
        if (oldest !== undefined) this.bySize.delete(oldest);
      }
      this.bySize.set(size, sprite);
    }
    return sprite;
  }
}

/** A cube icon drawn at `size` device pixels (plus a 1 px margin for antialiasing). */
export function drawCubeSprite(look: CubeLook, size: number): HTMLCanvasElement | null {
  const layer = createLayer(size + 2, size + 2);
  if (!layer) return null;
  drawCubeIcon(layer.ctx, look, (size + 2) / 2, (size + 2) / 2, size);
  return layer.canvas;
}

/** Font stack that has colour emoji on every platform. */
export const EMOJI_FONT =
  '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", system-ui, sans-serif';

/** An emoji drawn into a square of `size` device pixels. */
export function drawEmojiSprite(emoji: string, size: number): HTMLCanvasElement | null {
  const layer = createLayer(size, size);
  if (!layer) return null;
  const { ctx } = layer;
  ctx.font = `${Math.round(size * 0.78)}px ${EMOJI_FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(emoji, size / 2, size * 0.54);
  return layer.canvas;
}

/**
 * A name tag: emoji + name in bold white with a dark outline on a translucent pill.
 * `height` in device pixels.
 */
export function drawTagSprite(
  emoji: string,
  name: string,
  height: number,
): HTMLCanvasElement | null {
  const probe = createLayer(1, 1);
  if (!probe) return null;
  const font = `800 ${Math.round(height * 0.62)}px system-ui, -apple-system, "Segoe UI", sans-serif`;
  probe.ctx.font = font;
  const text = `${emoji} ${name}`;
  const textWidth = probe.ctx.measureText(text).width;
  const pad = height * 0.4;
  const layer = createLayer(textWidth + pad * 2, height);
  if (!layer) return null;
  const { ctx, canvas } = layer;
  const r = height / 2;
  ctx.fillStyle = 'rgba(10, 6, 20, 0.62)';
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.arcTo(canvas.width, 0, canvas.width, height, r);
  ctx.arcTo(canvas.width, height, 0, height, r);
  ctx.arcTo(0, height, 0, 0, r);
  ctx.arcTo(0, 0, canvas.width, 0, r);
  ctx.closePath();
  ctx.fill();
  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  ctx.lineWidth = Math.max(2, height * 0.12);
  ctx.strokeStyle = '#0a0614';
  ctx.strokeText(text, canvas.width / 2, height * 0.55);
  ctx.fillStyle = '#ffffff';
  ctx.fillText(text, canvas.width / 2, height * 0.55);
  return canvas;
}
